import { useState, useEffect } from 'react'
import { makeStyles } from '@mui/styles'
import NoSite from '../components/NoSite'
import { Box, Tab, Typography, Grid, CircularProgress } from '@mui/material'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import MetricsTimeSeriesChart from '../charts/Metrics/MetricsTimeSeriesChart'
import PerformanceComparisonParameter from '../components/PerformanceComparisonParameter'
import MetricsResidulChart from '../charts/Metrics/MetricsResidualChart'
import MetricsOnlineChart from '../charts/Metrics/MetricsOnlineChart'
import Api from '../api'
import { setSelectedUserSetup } from '../redux/actions/userSetups'
import { connect } from 'react-redux'
import PerformanceComparePanel from '../panels/PerformanceComparePanel'
import PerformanceCompareChart from '../charts/PerformanceCompare/PerformanceCompareTimeSeriesChart'
import mergeCompareData from '../utils/mergeCompareData'

const useStyles = makeStyles(theme => ({
  chartContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
  },
  subCaption: {
    color: theme.palette.primary.main,
    fontSize: '20px',
    fontWeight: '600',
  },
  paramContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '10px 0px 10px',
  },
  centerItem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
  },
}))

const PerformanceCompare = props => {
  const classes = useStyles(props)
  const {
    container,
    selectedSetup,
    setSelectedUserSetup,
    unit,
    timezone,
    selectedSetupPlant,
    issueTime,
    userPowerSetups,
  } = props

  // Use userPowerSetups from Redux, validate selectedSetup locally
  const userSetups = userPowerSetups
  const validatedSetup = userPowerSetups.includes(selectedSetup)
    ? selectedSetup
    : null

  // If there is only 1 available setup, select it automatically
  useEffect(() => {
    if (userSetups.length === 1 && selectedSetup !== userSetups[0]) {
      setSelectedUserSetup(userSetups[0])
    }
  }, [userSetups, selectedSetup, setSelectedUserSetup])

  const [loading, setLoading] = useState(false)
  const [mainTabValue, setMainTabValue] = useState('1')
  const [subTabValue, setSubTabValue] = useState('0')

  const [metricsData, setMetricsData] = useState(null)
  const [selectedPlant, setSelectedPlant] = useState(null)
  const disableButton = metricsData !== null || loading ? true : false

  const [displayChart, setDisplayChart] = useState([true, false, false])
  const [visibleChart, setVisibleChart] = useState(0)
  const [selectedForecast, setSelectedForecast] = useState(null)

  // for disabling inputs in form
  const [trackDisableStatus, setTrackDisableStatus] = useState(false)

  let initNodata = {
    heading: 'No setup selected',
    body: 'Select a setup and adjust parameters in order to start metrics',
  }

  if (validatedSetup) {
    initNodata = {
      heading: '',
      body: '',
    }
  }

  const [nodataContent, setNodataContent] = useState(initNodata)

  const handleMainTabChange = (_, newValue) => {
    setMainTabValue(newValue)
  }

  const handleSubTabChange = (_, newValue) => {
    const newState = [...displayChart]
    newState[newValue] = true
    setDisplayChart(newState)
    setVisibleChart(newValue)
    setSubTabValue(newValue)
  }

  const handlePlantChange = newPlant => {
    setSelectedPlant(newPlant)
  }

  const startMetricsData = async ({
    forecast,
    backtest,
    comparisonSelection,
  }) => {
    setLoading(true)
    setTrackDisableStatus(true)
    try {
      // Make both API calls in parallel
      const promises = [Api.Metrics.getMetrics(forecast)]
      if (backtest) {
        promises.push(Api.Metrics.getMetrics(backtest))
      }

      const [forecastResponse, backtestResponse] = await Promise.all(promises)

      // Check forecast response
      if (forecastResponse.status === 204) {
        setMetricsData(null)
        setTrackDisableStatus(false)
        setNodataContent({
          heading: `No data for plant ${forecast.plant || 'Unknown'} (S1)`,
          body: '',
        })
        return
      }

      // Check backtest response
      if (backtestResponse && backtestResponse.status === 204) {
        setMetricsData(null)
        setTrackDisableStatus(false)
        setNodataContent({
          heading: `No data for plant ${backtest?.plant || 'Unknown'} (S2)`,
          body: '',
        })
        return
      }

      if (
        forecastResponse.status === 200 &&
        (!backtestResponse || backtestResponse.status === 200)
      ) {
        const forecastData = forecastResponse?.data
        const backtestData = backtestResponse?.data || null

        if (!forecastData || !forecastData.data) {
          setNodataContent({
            heading: `Data issue for setup (S1): ${selectedSetup}`,
            body: 'We received an invalid dataset from the server.',
          })
          setMetricsData(null)
          setTrackDisableStatus(false)
        } else {
          // Merge S1 and S2 data arrays by Timestamp.
          // Uses the UNION of all keys from both datasets (not just common ones).
          // If a key exists only in one dataset, the other side gets null.
          // Keys are collected from the first row of each array.
          const mergedData = mergeCompareData(
            forecastData.data,
            backtestData?.data,
          )

          setMetricsData({
            forecast: forecastData,
            backtest: backtestData,
            mergedData: mergedData,
            resolution: forecastData.resolution,
            comparisonSelection,
          })
        }
      }
    } catch (error) {
      setMetricsData(null)
      setTrackDisableStatus(false)
      const status = error?.response?.status
      if (status === 411) {
        setNodataContent({
          heading: `Error running query for selected setup: ${selectedSetup}`,
          body: '',
        })
      } else if (status === 404) {
        setNodataContent({
          heading: `No data for selected period. Please check your selection.`,
          body: '',
        })
      } else {
        setNodataContent({
          heading: `Error fetching data for selected setup: ${selectedSetup}`,
          body: '',
        })
      }
      console.error('Error fetching Compare data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleStartMetric = metricsObj => {
    startMetricsData(metricsObj)
  }

  const handleNoDataContentUpdate = newContent => {
    setNodataContent(newContent)
    setMetricsData(null)
  }

  /** This code will activate reset metricsData if user change the unit or timezone inside Logout component */
  useEffect(() => {
    setMetricsData(null)
  }, [timezone, unit])

  /*
  This function is separated from the tab context so that you don't have 
  to render the charts again while switching between tabs
  */

  const panels = () => {
    return (
      <>
        {displayChart[0] && (
          <div
            value="0"
            style={{
              padding: '0',
              display: visibleChart == '0' ? 'block' : 'none',
            }}
          >
            <Grid container spacing={0}>
              <Grid item md={12}>
                <PerformanceCompareChart
                  chartDataRaw={metricsData.mergedData}
                  resolution={metricsData.resolution}
                  chartName={'chartMetricsTimeSeries'}
                  selectedSetup={selectedSetup}
                  comparisonSelection={metricsData.comparisonSelection}
                  setTrackDisableStatus={setTrackDisableStatus}
                  issueTime={issueTime}
                />
              </Grid>
            </Grid>
          </div>
        )}

        {displayChart[1] && (
          <div
            value="1"
            style={{
              padding: '0',
              display: visibleChart == '1' ? 'block' : 'none',
            }}
          >
            {mainTabValue === '1' ? (
              <Grid container spacing={0} key={mainTabValue}>
                <Grid item md={6}>
                  <MetricsResidulChart
                    chartData={metricsData.forecast?.data}
                    chartName={'chartMetricsResidualOnline'}
                    chartType={'online'}
                    setTrackDisableStatus={setTrackDisableStatus}
                  ></MetricsResidulChart>
                </Grid>
                <Grid item md={6}>
                  <MetricsOnlineChart
                    chartData={metricsData.forecast?.data}
                    chartName={'chartMetricsOnlineOnline'}
                    chartType={'online'}
                    setTrackDisableStatus={setTrackDisableStatus}
                  ></MetricsOnlineChart>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={0} key={mainTabValue}>
                <Grid item md={6}>
                  <MetricsResidulChart
                    chartData={metricsData.forecast?.data}
                    chartName={'chartMetricsResidualMetering'}
                    chartType={'metering'}
                    setTrackDisableStatus={setTrackDisableStatus}
                  ></MetricsResidulChart>
                </Grid>
                <Grid item md={6}>
                  <MetricsOnlineChart
                    chartData={metricsData.forecast?.data}
                    chartName={'chartMetricsOnlineMetering'}
                    chartType={'metering'}
                    setTrackDisableStatus={setTrackDisableStatus}
                  ></MetricsOnlineChart>
                </Grid>
              </Grid>
            )}
          </div>
        )}
        {/* {displayChart[2] && (
          <div
            value="2"
            style={{ display: visibleChart == '2' ? 'block' : 'none' }}
          >
            <Grid container spacing={0}>
              <Grid item md={12}>
                <MetricsTimeSeriesChart
                  chartData={metricsData.forecast}
                  resolution={metricsData.forecast?.resolution}
                  chartName={'chartMetricsTimeSeries33'}
                />
              </Grid>
            </Grid>
          </div>
        )} */}
      </>
    )
  }

  const resultsCharts = () => {
    return (
      <>
        <Typography className={classes.subCaption}>
          FORECAST VS. OBSERVATIONS
        </Typography>
        <TabContext value={subTabValue}>
          <Box sx={{ borderBottom: 0 }}>
            <TabList
              onChange={handleSubTabChange}
              aria-label="sub tabs for Online"
              sx={{
                '& .MuiTabs-indicator': { backgroundColor: '#368ED8' },
                '& .MuiTab-root.Mui-selected': { color: '#368ED8' },
                '& .MuiTab-root': {
                  minWidth: '160px',
                },
              }}
            >
              <Tab label="Time Series Plot" value="0" />
              {/* <Tab label="Residual Plot" value="1" /> */}
              {/* {selectedForecast === 'aggregated' && (
                <Tab label="Coverage Plot" value="2" />
              )} */}
            </TabList>
          </Box>
        </TabContext>
        {panels()}
      </>
    )
  }

  const resultPanel = () => {
    return metricsData !== null ? (
      <>
        <TabContext value={mainTabValue}>
          <Box sx={{ borderBottom: 0 }}>
            <TabList
              onChange={handleMainTabChange}
              aria-label="Metrics charts"
              sx={{
                '& .MuiTabs-indicator': { backgroundColor: '#368ED8' },
                '& .MuiTab-root.Mui-selected': { color: '#368ED8' },
                '& .MuiTab-root': {
                  minWidth: '160px',
                },
              }}
            >
              <Tab label="Online" value="1" />
              <Tab label="Metering" value="2" />
            </TabList>
          </Box>

          <TabPanel value="1" sx={{ p: 0 }}>
            <div className={classes.paramContainer}>
              <PerformanceComparisonParameter
                label={'nmae'}
                value={metricsData.forecast?.OnlineNmae}
                compareValue={metricsData.backtest?.OnlineNmae}
              />
              <PerformanceComparisonParameter
                label={'nrmse'}
                value={metricsData.forecast?.OnlineNrmse}
                compareValue={metricsData.backtest?.OnlineNrmse}
              />
              <PerformanceComparisonParameter
                label={'nbias'}
                value={metricsData.forecast?.OnlineNBias}
                compareValue={metricsData.backtest?.OnlineNBias}
              />
              <PerformanceComparisonParameter
                label={'mae'}
                value={metricsData.forecast?.OnlineMae}
                compareValue={metricsData.backtest?.OnlineMae}
              />
              <PerformanceComparisonParameter
                label={'rmse'}
                value={metricsData.forecast?.OnlineRmse}
                compareValue={metricsData.backtest?.OnlineRmse}
              />
              <PerformanceComparisonParameter
                label={'bias'}
                value={metricsData.forecast?.OnlineBias}
                compareValue={metricsData.backtest?.OnlineBias}
              />
            </div>
          </TabPanel>

          <TabPanel value="2" sx={{ p: 0 }}>
            <div className={classes.paramContainer}>
              <PerformanceComparisonParameter
                label={'nmae'}
                value={metricsData.forecast?.MeteringNmae}
                compareValue={metricsData.backtest?.MeteringNmae}
              />
              <PerformanceComparisonParameter
                label={'nrmse'}
                value={metricsData.forecast?.MeteringNrmse}
                compareValue={metricsData.backtest?.MeteringNrmse}
              />
              <PerformanceComparisonParameter
                label={'nbias'}
                value={metricsData.forecast?.MeteringNBias}
                compareValue={metricsData.backtest?.MeteringNBias}
              />
              <PerformanceComparisonParameter
                label={'mae'}
                value={metricsData.forecast?.MeteringMae}
                compareValue={metricsData.backtest?.MeteringMae}
              />
              <PerformanceComparisonParameter
                label={'rmse'}
                value={metricsData.forecast?.MeteringRmse}
                compareValue={metricsData.backtest?.MeteringRmse}
              />
              <PerformanceComparisonParameter
                label={'bias'}
                value={metricsData.forecast?.MeteringBias}
                compareValue={metricsData.backtest?.MeteringBias}
              />
            </div>
          </TabPanel>
        </TabContext>
        {resultsCharts()}
      </>
    ) : (
      <NoSite
        {...props}
        creating={false}
        heading={nodataContent.heading}
        body={nodataContent.body}
      ></NoSite>
    )
  }

  return (
    <>
      <PerformanceComparePanel
        container={container}
        userSetups={userSetups}
        selectedPlant={selectedPlant}
        setSelectedPlant={handlePlantChange}
        setMetricsParameters={handleStartMetric}
        handleNoDataContentUpdate={handleNoDataContentUpdate}
        setMetricsData={setMetricsData}
        onChangeSelectedForecast={selVal => setSelectedForecast(selVal.name)}
        disableButton={disableButton}
        trackDisableStatus={trackDisableStatus}
      >
        {!loading ? (
          resultPanel()
        ) : (
          <div className={classes.centerItem}>
            <CircularProgress />
          </div>
        )}
      </PerformanceComparePanel>
    </>
  )
}

const mapStateToProps = state => ({
  selectedSetup: state.userSetups.selectedSetup,
  userPowerSetups: state.userSetups.userPowerSetups,
  unit: state.user.unit,
  timezone: state.user.timezone,
  selectedSetupPlant: state.metrics.selectedPlant,
  issueTime: state.metrics.issueTime,
})

const mapDispatchToProps = {
  setSelectedUserSetup,
}

export default connect(mapStateToProps, mapDispatchToProps)(PerformanceCompare)
